import React, { useState, useEffect } from 'react';
import styles from './profile-interests.module.css';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../success-message/success-message';
import { useAppSelector } from '../../services/store/hooks';
import { useUpdateProfileInterestsMutation } from '../../services/store/api/userApi';
import { TCategory } from '../../types';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

interface Props {
  fromShow?: 'rega' | 'profile';
}

const ProfileInterests: React.FC<Props> = ({ fromShow = 'rega' }) => {
  const navigate = useNavigate();

  const { categories } = useAppSelector(state => state.dict);
  const { profile } = useAppSelector(state => state.user);

  const [formData, setFormData] = useState<{ interests: number[] }>({
    interests: [],
  });

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const btnSubmit = fromShow === 'rega' ? 'Продолжить' : 'Сохранить';

  const [
    updateProfileInterests,
    { isLoading, isError, error },
  ] = useUpdateProfileInterestsMutation();

  /**
   * Синхронизация interests после загрузки profile
   */
  useEffect(() => {
    if (profile?.interests) {
      setFormData({
        interests: profile.interests.map(i => i.id),
      });
    }
  }, [profile]);

  /**
   * Переключение интереса
   */
  const handleInterestToggle = (interestId: number) => {
    setValidationError(null);

    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId],
    }));
  };

  /**
   * Submit
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.interests.length) {
      setValidationError('Выберите хотя бы один интерес');
      return;
    }

    setValidationError(null);

    try {
      await updateProfileInterests(formData).unwrap();

      if (fromShow === 'rega') {
        navigate('/profile-lifestyle');
      } else {
        setShowSuccessMessage(true);

        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      }
    } catch {
      // ошибка отобразится через isError
    }
  };

  /**
   * Безопасная защита от undefined categories
   */
  if (!categories?.length) {
    return null; // можно заменить на Loader
  }

  /**
   * Типизированная ошибка RTK Query
   */
  const errorMessage =
    (error as FetchBaseQueryError & {
      data?: { message?: string };
    })?.data?.message ?? 'Ошибка при сохранении интересов';

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.form}>
        {categories.map((category: TCategory) => (
          <div key={category.id} className="interestsGroup">
            <div className={styles.categoryTitle}>
              {category.title}
            </div>

            <div className="interestsContainer">
              {category.interests.map(interest => (
                <button
                  key={interest.id}
                  type="button"
                  className={`interestBtn ${
                    formData.interests.includes(interest.id)
                      ? 'interestBtnActive'
                      : ''
                  }`}
                  onClick={() =>
                    handleInterestToggle(interest.id)
                  }
                >
                  {interest.title}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className="btn-container mt-10">
          <button
            type="submit"
            className="btn-link"
            disabled={isLoading}
          >
            {isLoading ? 'Сохранение...' : btnSubmit}
          </button>
        </div>
      </form>

      {showSuccessMessage && (
        <SuccessMessage>
          Интересы успешно сохранены!
        </SuccessMessage>
      )}

      {(validationError || isError) && (
        <div className="error-box">
          {validationError ?? errorMessage}
        </div>
      )}
    </>
  );
};

export default ProfileInterests;
